import React, { FunctionComponent, memo, useCallback } from "react";
import { Flex, useColorModeValue, Box, Badge } from "@chakra-ui/react";
import { format } from "d3";
import { isEqual } from "lodash";

import { controlReasonHumanLabel } from "../../../dashboard/utils";
import { useControlUI } from "../../ControlUIContext";

import SourceReports from "./SourceReports";
import SourceMetrics from "./SourceMetrics";
import SourceNameAndUnit from "./SourceNameAndUnit";
import SourcePower from "./SourcePower";
import SourceProgress from "./SourceProgress";

import {
  PlcSource,
  PlcMetrics,
} from "../types";


interface IProps extends PlcSource {
  name: string;
  outOfBounds?: boolean;
  offline: boolean;
  metrics: PlcMetrics;
}


export const reasonablePrecision = (value: number | null, decimals: number = 1): number | string => {
  if (value === null) return "...";

  const abs = Math.abs(value);

  if (abs === 0 ) return value;
  else if (abs < 10) return format(`.${Math.max(0, decimals)}f`)(value);
  else if (abs < 100) return format(`.${Math.max(0, decimals - 1)}f`)(value);
  else if (abs > 1000000) return `${ reasonablePrecision(value / 1000000)}M`;
  else if (abs > 1000) return `${ reasonablePrecision(value / 1000)}k`;
  else return Math.round(value);
};


const Source: FunctionComponent<IProps> = memo(function Source({
  name,
  reports,
  power,
  outOfBounds,
  offline,
  // status,
  target,
  reason,
  metrics,
  children,
}) {
  const { expandedReports, expandReports } = useControlUI();
  const toggleExpanded = useCallback(() => expandReports(!expandedReports), [expandedReports, expandReports]);
  const offlineColor = useColorModeValue("black", "white");
  const controlBackColor = useColorModeValue("black", "white");
  const controlFrontColor = useColorModeValue("white", "black");
  const offlineFrontColor = useColorModeValue("white", "black");
  const outOfBoundsColor = useColorModeValue("red.300", "red.700");
  const withinBoundsColor = useColorModeValue("gray.100", "gray.800");

  return <>
    <Flex
      flexDirection="column"
      justifyContent="space-between"
      px={6}
      py={3}
      mx={2}
      mb={5}
      color={offline ? offlineFrontColor : "inherit"}
      bg={offline ? offlineColor : outOfBounds ? outOfBoundsColor : withinBoundsColor}
      borderColor="initial"
      borderWidth={reason ? 4 : 0}
      width={"250px"}
      position="relative"
      onClick={toggleExpanded}
      cursor="pointer"
    >
      <Flex
        flexDirection="column"
      >
        <SourcePower value={metrics?.kw_active_power} target={target} outOfBounds={outOfBounds} /> 
        <SourceNameAndUnit name={name} unit="kW" />
        <SourceProgress value={power} offline={offline} />
        {offline && <Box fontSize={18} fontWeight="bold" color="red" textAlign="right" mt={16}></Box>}
        <SourceMetrics>
          {children}
        </SourceMetrics>
      </Flex>
      <SourceReports reports={reports} />
      <Badge
        position="absolute"
        top={"-2px"}
        left={"-2px"}
        background={controlBackColor}
        color={controlFrontColor}
        title={`${controlReasonHumanLabel(reason)}`}
      >
        {reason}
      </Badge>
    </Flex>
  </>
}, isEqual);

export default Source;
